import { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import Icon from './base/Icon';
import Keybinds from './base/Keybinds';
import Link from './base/Link';
import { Div, StyledUtilsProps } from './helpers/StyledUtils';

interface Props extends StyledUtilsProps {
  onChange: (value: string) => void;
  onSubmit?: (value: string) => void;
  value: string;
  onBlur?: () => void;
  placeholder?: string;
  name?: string;
  focus?: boolean;
  small?: boolean;
  borderless?: boolean;
  disabled?: boolean;
  search_ref?: React.RefObject<HTMLInputElement | null>;
  add_keybinds?: boolean;
  submitted?: boolean;
}

const StyledWrapper = styled(Div)<{ borderless: boolean }>(
  ({ theme, borderless }) => css`
    position: relative;
    display: flex;
    align-items: center;

    label {
      position: absolute;
      left: ${theme.spacing(3)};
      margin: 0;
      display: flex;
      align-items: center;
      svg {
        vertical-align: middle;
      }
    }
    button {
      position: absolute;
      margin-right: ${theme.spacing(2)};
      padding: 4px;
      border-radius: 50%;
      right: 0;
    }

    kbd {
      position: absolute;
      margin-right: ${theme.spacing(2)};
      right: 0;
    }

    ${borderless &&
    css`
      &:focus-within {
        input {
          outline: none;
          border: none;
          box-shadow: none;
        }
      }
    `}
  `,
);

const StyledSearchInput = styled.input<{ small?: boolean; borderless: boolean }>(
  ({ theme, small, borderless }) => css`
    width: 100%;
    box-sizing: border-box;
    line-height: ${theme.pxToRem(theme.font_sizes.m.line_height)};
    padding: ${theme.spacing(small ? 1 : 1.5)} ${theme.pxToRem(36)};
    border: none;
    box-shadow: ${theme.shadows.input.neutral};
    ${borderless &&
    css`
      padding: ${theme.spacing(small ? 1.5 : 2.5)} ${theme.pxToRem(36)};
      border: none;
      border-radius: 0;
      box-shadow: none;
    `}

    &:focus {
      outline: none;
      box-shadow: ${theme.shadows.input.focused};
    }

    &:disabled {
      background-color: ${theme.colors.surface.base.disabled};
      color: ${theme.colors.on.neutral.disabled};
      cursor: not-allowed;
    }
  `,
);

const Search = ({
  onChange,
  onSubmit,
  onBlur,
  placeholder,
  name,
  value,
  focus,
  small,
  borderless,
  disabled,
  add_keybinds = false,
  submitted,
  search_ref: s_ref,
  ...props
}: Props) => {
  const internal_ref = useRef<HTMLInputElement | null>(null);
  const search_ref = s_ref || internal_ref;

  useEffect(() => {
    if (focus) {
      if (search_ref.current) {
        search_ref.current?.focus({
          preventScroll: true,
        });
      }
    }
  }, [search_ref?.current]);

  return (
    <StyledWrapper {...props} borderless={!!borderless}>
      <label htmlFor={name || 'search'}>
        <Icon icon="search" muted />
      </label>
      <StyledSearchInput
        borderless={!!borderless}
        small={small}
        ref={search_ref}
        id={name || 'search'}
        placeholder={placeholder || 'Search'}
        value={value || ''}
        disabled={disabled}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (onSubmit && e.key === 'Enter') {
            e.preventDefault();
            onSubmit(e.currentTarget.value);
          }
        }}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
      {value && !disabled && (submitted === undefined || submitted) ? (
        <Link
          as="button"
          type="button"
          icon="cancel"
          neutral
          p={{ x: 0.5, y: 0 }}
          onClick={() => {
            onChange('');
            onSubmit?.('');
          }}
        />
      ) : (
        !!value &&
        add_keybinds && <Keybinds keys={[<Icon key="keyboard_return" icon="keyboard_return" />]} />
      )}
    </StyledWrapper>
  );
};

export default Search;
